import type { ZoneId } from '../types';

/**
 * NPC survivor names with a gender for their portrait (plan v0.6, B2). 13. 10. 2026 (owner): they read as other players,
 * English usernames that pass the player name rules (`nameIssue`), no Balkan first names; none is a world book name
 * (Doc Mara, Tobias, Victor Hale, Garrity, Danny Dawn). 48 names, the genders alternating.
 */
export const NPC_NAME_LIST: readonly { name: string; gender: 'm' | 'f' }[] = [
  { name: 'ashwren', gender: 'f' }, { name: 'wes_cole', gender: 'm' }, { name: 'Juno_K', gender: 'f' }, { name: 'Grimsby', gender: 'm' },
  { name: 'tamsin_v', gender: 'f' }, { name: 'oakley', gender: 'm' }, { name: 'RubyHollow', gender: 'f' }, { name: 'RustyNails', gender: 'm' },
  { name: 'bexx', gender: 'f' }, { name: 'Finn_O', gender: 'm' }, { name: 'nell88', gender: 'f' }, { name: 'bram_t', gender: 'm' },
  { name: 'quietfern', gender: 'f' }, { name: 'deadeye_jim', gender: 'm' }, { name: 'Wren', gender: 'f' }, { name: 'Hank', gender: 'm' },
  { name: 'MaggieRust', gender: 'f' }, { name: 'cobb', gender: 'm' }, { name: 'ivy-on-fire', gender: 'f' }, { name: 'MilesAhead', gender: 'm' },
  { name: 'lottie', gender: 'f' }, { name: 'ollie77', gender: 'm' }, { name: 'Sable', gender: 'f' }, { name: 'Sully', gender: 'm' },
  { name: 'kit_carver', gender: 'f' }, { name: 'nate_runs', gender: 'm' }, { name: 'HazelGrey', gender: 'f' }, { name: 'ColdHarbor', gender: 'm' },
  { name: 'rosie_mac', gender: 'f' }, { name: 'jasper_k', gender: 'm' }, { name: 'piper92', gender: 'f' }, { name: 'Tucker', gender: 'm' },
  { name: 'EmberLane', gender: 'f' }, { name: 'Gus', gender: 'm' }, { name: 'clara_b', gender: 'f' }, { name: 'hollow_ben', gender: 'm' },
  { name: 'Vex', gender: 'f' }, { name: 'ReedK', gender: 'm' }, { name: 'mossy', gender: 'f' }, { name: 'archie', gender: 'm' },
  { name: 'Tess_W', gender: 'f' }, { name: 'Mack', gender: 'm' }, { name: 'jo_sawyer', gender: 'f' }, { name: 'nash22', gender: 'm' },
  { name: 'Rook', gender: 'f' }, { name: 'ozzie', gender: 'm' }, { name: 'Iris', gender: 'f' }, { name: 'Kaz', gender: 'm' },
];
/** The names alone, in the same order (NPC survivors and Militia pick from this). */
export const NPC_NAMES: readonly string[] = NPC_NAME_LIST.map((n) => n.name);

/**
 * The NPC groups. Package 4, part 0 (owner, 7. 10. 2026): the world book's factions (`was`: the name before, for the
 * saved database's one-time rename): the Foundry holds Calder Steelworks, the Tollmen Downtown (Route 9 cannot be held;
 * in texts they stay "the people of Route 9"), The Mended hold nothing (rumors only until season 2).
 */
export const NPC_CLANS = [
  { name: 'Foundry', tag: 'FDRY', was: 'Iron Choir', zone: 'factory' as ZoneId, description: 'They hold the steelworks and make the guns everyone else uses. Welded plate and soot. Led by Victor Hale, the Warlord.', emblem: { shape: 'circle', symbol: 'gear', field: '#1F1B14', ink: '#C8483A' } },
  { name: 'Tollmen', tag: 'TOLL', was: 'The Ravens', zone: 'downtown' as ZoneId, description: 'The people of Route 9. They hold the roads and charge for passage, and keep a deal while you pay. Led by Old Man Garrity.', emblem: { shape: 'shield', symbol: 'road', field: '#1F1B14', ink: '#E8A33D' } },
  { name: 'The Mended', tag: 'MEND', was: 'Dust Saints', zone: null, description: 'Nobody has met them. People say they carry scars and leave food for the Mutants.', emblem: { shape: 'diamond', symbol: 'crow', field: '#E8E2D0', ink: '#1F1B14' } },
];

export interface RumorTemplate {
  zone: ZoneId;
  text: string;
  /** Kind of discovery bonus the rumor points at. */
  kind: 'item' | 'secret' | 'raiders' | 'loot';
}

/**
 * Hand Radio (krug 5, F10): the reliability a player sees. A true rumor looks `bonus` levels
 * more reliable (at most 3); a false one stays as it is, so it stands out. Truth never changes.
 */
export function seenReliability(reliability: 1 | 2 | 3, isFalse: boolean, bonus: number): 1 | 2 | 3 {
  if (isFalse || bonus <= 0) return reliability;
  return Math.min(3, reliability + Math.round(bonus)) as 1 | 2 | 3;
}

/**
 * Package 4, part 0 (owner, 7. 10. 2026): the rumors in the world book's voices, Danny Dawn on the radio (exclamation
 * marks) or a survivor's whisper. The Mended show up only here until season 2. The kinds stay as they were.
 */
export const RUMOR_TEMPLATES: RumorTemplate[] = [
  { zone: 'forest', text: 'Somebody found a civil defense bunker under Calder Woods. The door still has the city crest.', kind: 'secret' },
  { zone: 'forest', text: "A hunter swears the old fire tower sits on a cache. He won't say whose.", kind: 'loot' },
  { zone: 'gas_station', text: 'Traffic update, Mercy Falls! Tollmen are moving fuel off Route 9 after dark. Drive safe!', kind: 'raiders' },
  { zone: 'gas_station', text: "The car wash on Route 9 has a basement. Even the Tollmen don't go down there.", kind: 'loot' },
  { zone: 'downtown', text: "A trader with a dog is buying wire near the ring road. Not Tobias. Tobias doesn't share customers.", kind: 'loot' },
  { zone: 'downtown', text: 'Shopping news, folks! The pawn shop on Fifth still has a rifle in the window!', kind: 'item' },
  { zone: 'downtown', text: "Someone pinned Halcyon floor plans in the metro. The bottom floor isn't on the elevator.", kind: 'secret' },
  { zone: 'hospital', text: 'The east wing of Mercy General still has power. Nobody asks for whom.', kind: 'loot' },
  { zone: 'hospital', text: "Health tip from Danny! There's a crate of medkits in the Mercy General morgue. Bring a friend!", kind: 'item' },
  { zone: 'factory', text: "WARDEN's Patrols skip the Steelworks for an hour after midnight. Maintenance, they say.", kind: 'loot' },
  { zone: 'factory', text: "The Foundry's forewoman keeps a city map on her wall. The pins are in places nobody goes.", kind: 'secret' },
  { zone: 'military_base', text: "Good news from Fort Halden! A gate's been open since the storm. WARDEN calls it a scheduled opportunity!", kind: 'item' },
  { zone: 'military_base', text: "There's a Guard armory crate in the Fort Halden motor pool. The paperwork outlived the soldiers.", kind: 'item' },
  { zone: 'mountains', text: 'Smoke from the sanatorium again. Someone is living in the locked wing.', kind: 'secret' },
  { zone: 'mountains', text: 'People with scars leave food at the edge of Pine Ridge. For the Mutants.', kind: 'loot' },
  { zone: 'wasteland', text: 'Sightseeing tip! A caravan crossed The Burn last night carrying something that glowed. Pack a mask!', kind: 'item' },
];

/** Package 4, part 0: the rumor when a zone has none of its own left (a survivor's whisper at Station 9). */
export const RUMOR_FALLBACK = 'Word at Station 9: someone found something worth whispering about near {zone}.';

export type WorldEventKind = 'acid_rain' | 'convoy' | 'radiation_storm' | 'supply_drop' | 'fog';

export interface WorldEventDef {
  kind: WorldEventKind;
  title: string;
  text: string;
  zones: ZoneId[];
  hours: [number, number];
  /** Zone modifiers while active. */
  mods: { duration?: number; loot?: number; danger?: number; raiders?: number; encounter?: number };
  accent: 'danger' | 'info' | 'accent' | 'safe';
}

export const WORLD_EVENTS: Record<WorldEventKind, WorldEventDef> = {
  acid_rain: {
    // Package 4, part 0 (owner): the world book's events; the effects stay.
    kind: 'acid_rain', title: 'Chemical rain over {zone}', text: "WARDEN's cleanup drones are spraying it. Runs there take 25% longer until it clears.",
    zones: ['factory', 'downtown', 'gas_station'], hours: [2, 5], mods: { duration: 0.25 }, accent: 'info',
  },
  convoy: {
    kind: 'convoy', title: 'A Tollmen convoy near {zone}', text: 'Tollmen moving cargo. More fights, better loot.',
    zones: ['gas_station', 'downtown', 'military_base'], hours: [2, 4], mods: { raiders: 1.5, loot: 0.2 }, accent: 'accent',
  },
  radiation_storm: {
    kind: 'radiation_storm', title: 'Ash storm in {zone}', text: 'Visibility: none. Danger +1 while it lasts.',
    zones: ['wasteland', 'military_base', 'factory'], hours: [3, 6], mods: { danger: 1, loot: 0.15 }, accent: 'danger',
  },
  supply_drop: {
    kind: 'supply_drop', title: 'Quarantine aid over {zone}', text: 'WARDEN still drops aid on schedule. Everyone is going for it.',
    zones: ['forest', 'hospital', 'downtown', 'mountains'], hours: [1, 3], mods: { loot: 0.3, encounter: 1.4 }, accent: 'safe',
  },
  fog: {
    kind: 'fog', title: 'Thick fog in {zone}', text: 'Easier to hide. Harder to see who is following you.',
    zones: ['forest', 'hospital', 'mountains'], hours: [2, 4], mods: { encounter: 0.8 }, accent: 'info',
  },
};

/** Rotation of horde targets and bosses. */
export const HORDE_ZONES: ZoneId[] = ['downtown', 'hospital', 'gas_station', 'factory', 'forest', 'military_base'];

/**
 * 1. 10. 2026 (C3): what a horde gives besides the old tiers (taking part, 25%, 50%, the top 10%), so the
 * energy of a wave always brings something and a player outside the top 10% has goals of their own.
 * `on: false` (simulations, VARIANT=a and b) is the horde of 30. 9.
 */
export const HORDE_RULES = {
  on: true,
  /** Every wave played also searches the zone: this many resource finds, like a short run. */
  waveFinds: 2,
  /**
   * Personal damage thresholds, as shares of `hordePersonalWave` at the survivor's level. In `sim:progress`
   * (one wave a horde) a wave does 0.5–0.7 of it at the median, a quarter of the waves 0.85–1.1: one wave
   * reaches the first about 3 times in 4, the second about half the time and the third 15–35% of the time
   * by level band; two waves reach all three for most players (a horde has four waves).
   */
  personal: [0.35, 0.65, 0.95],
  /** Personal rewards: the first Scrap and Bandages, the second materials for the level, the third an item. */
  personalScrap: 30,
  personalBandages: 2,
  personalDays: 1,
  personalRarity: 'rare' as const,
  /** 75% of the horde down: everyone who fought gets materials worth this many daily supplies. */
  p75Days: 1,
  /** The horde broken: everyone who fought gets an item of this rarity. */
  victoryRarity: 'rare' as const,
};

/** One wave's damage at a level, as the horde's size counts it: 5 ticks × (40 + 18 × level) × 6. */
export const hordeWaveDamage = (level: number) => 30 * (40 + 18 * Math.max(1, level));

/**
 * C3: what one wave of a survivor does at a level, measured in `sim:progress` (1. 10. 2026: about 1,500 at
 * level 5, 6,000 at 12, 20,000 at 18; ×1.22 a level). Steeper than the horde's own size estimate above,
 * which stays as it is (the horde's health does not change).
 */
export const hordePersonalWave = (level: number) => 1500 * Math.pow(1.22, Math.max(1, level) - 5);

/** C3: the three personal damage thresholds at a level, rounded to 100. */
export const hordePersonalThresholds = (level: number) => HORDE_RULES.personal.map((w) => Math.max(100, Math.round((hordePersonalWave(level) * w) / 100) * 100));

/**
 * 2. 10. 2026 (package 1, 10.b): the horde is sized by the survivors who really fight, not by everyone who
 * played in the last 3 days. It expects the average of real fighters of the last `history` hordes ×
 * `headroom` (at least 1, at most `maxStep` up or down from the last horde's), each giving `waves` waves of
 * the damage a real wave did in those hordes (`hordeWaveEstimate`) × `needShare`; a boss wave counts
 * `bossShare` of a horde wave. The militia makes up the rest, at least 45–62% of the horde, and the horde
 * is never smaller than `floor` (the militia fills it). The zone is only hurt by a defeat if at least
 * `penaltyMinFighters` real survivors fought; with fewer than `epicMinFighters` real survivors the Epic
 * goes to everyone who reaches the third personal threshold (the top 10% keeps the title). Unclaimed
 * rewards are paid into Storage when the next horde starts (10.j, `autoPay`). `on: false` (sims, VARIANT d
 * and older): the horde before (40,000 or more from everyone who played in 3 days).
 *
 * Why the measured wave and not `hordePersonalWave` (the sizing sim, BALANCE_REPORT package 1): a real wave
 * does 0.9 of that yardstick at level 10 and 0.5 at level 20, so one share of it made the horde easy in the
 * middle and nearly unbeatable at the top. `waveShare` × the yardstick is only the prior while the last
 * hordes saw few waves (`prior` waves' worth).
 */
export const HORDE_SIZE_RULES = {
  on: true,
  history: 7,
  headroom: 1.3,
  maxStep: 0.3,
  waves: 2,
  /** Calibrated with `pnpm sim:horde` (55–73% of nights won when the fighters give 2 waves; BALANCE_REPORT). */
  needShare: 0.72,
  waveShare: 0.6,
  prior: 2,
  /** A boss wave (Mutants) does about 0.66–0.72 of a horde wave (Infected) at the same level (`pnpm sim:horde`). */
  bossShare: 0.68,
  floor: 10_000,
  penaltyMinFighters: 3,
  epicMinFighters: 10,
  autoPay: true,
};

/** The real fighters a horde expects: the last hordes' average × headroom, at least 1, at most ±maxStep from the last one's. */
export function hordeAssumedFighters(recent: readonly number[], previous: number | null): number {
  const avg = recent.length ? recent.reduce((a, n) => a + n, 0) / recent.length : 0;
  const want = Math.max(1, avg * HORDE_SIZE_RULES.headroom);
  if (previous == null || previous <= 0) return Math.round(want * 10) / 10;
  const lo = Math.max(1, previous * (1 - HORDE_SIZE_RULES.maxStep));
  const hi = previous * (1 + HORDE_SIZE_RULES.maxStep);
  return Math.round(Math.min(hi, Math.max(lo, want)) * 10) / 10;
}

/**
 * What one real wave did in the last hordes: their real survivors' damage over their waves, with `prior`
 * waves of `waveShare` × `hordePersonalWave` at the fighters' average level, so a few waves cannot swing it.
 */
export function hordeWaveEstimate(damage: number, waves: number, level: number): number {
  const prior = HORDE_SIZE_RULES.prior;
  return Math.round((damage + prior * HORDE_SIZE_RULES.waveShare * hordePersonalWave(level)) / (waves + prior));
}

/** The damage the real fighters must deal to break a horde (or a boss), from the expected fighters and a wave's damage. */
export function hordeRealNeed(assumed: number, perWave: number, boss: boolean): number {
  return Math.round(assumed * HORDE_SIZE_RULES.waves * perWave * HORDE_SIZE_RULES.needShare * (boss ? HORDE_SIZE_RULES.bossShare : 1));
}

/** The horde's health and the militia's part: the militia covers `militiaShare` or more, the floor is filled by it too. */
export function hordeSize(realNeed: number, militiaShare: number): { hpTotal: number; npcBudget: number } {
  const hpTotal = Math.max(HORDE_SIZE_RULES.floor, Math.round(realNeed / Math.max(0.05, 1 - militiaShare) / 1000) * 1000);
  return { hpTotal, npcBudget: Math.max(0, hpTotal - realNeed) };
}

/** The militia's share of a horde for the forecast (the middle of 45–62%). */
export const HORDE_MILITIA_MID = 0.535;

/**
 * Package 4, part 0 (owner, 7. 10. 2026): the Sunday bosses of the world book, each in its own zone of the horde's rotation
 * (Calder Woods never: a defeat there would raise the danger for new players). The order of the Sundays stays. `tide`: what
 * the boss fights as (the same ×20 elite tide as before): the Brute a Mutant, the Warlord a band of raiders (the Foundry),
 * the Hive many weak Infected (a big group with smaller hits, the Mutant tide's HP).
 */
export const BOSSES = [
  { id: 'brute', name: 'The Brute', title: 'The Brute, the oldest Mutant, came up out of {zone}', zone: 'hospital' as ZoneId, tide: { type: 'mutant' as const, count: 3 } },
  { id: 'warlord', name: 'The Warlord', who: 'The Warlord, Victor Hale', title: 'Victor Hale, the Warlord, leads the Foundry through {zone}', zone: 'factory' as ZoneId, tide: { type: 'raider' as const, count: 3 } },
  { id: 'hive', name: 'The Hive', title: 'The Hive, Infected grown into one mass, has swallowed {zone}', zone: 'downtown' as ZoneId, tide: { type: 'infected' as const, count: 6, attackMult: 0.7, hpAs: 'mutant' as const } },
];

/** Atmospheric lines for ticks where nothing important happens. */
export const QUIET_LINES = [
  'Nothing. Just wind and the tick of cooling metal.',
  'Waited out a patrol of nobody in particular.',
  'Checked a car. Checked another. Kept walking.',
  'Found a note: "Sorry." Nothing else.',
  'Took the long way round. Dogs on the short one.',
];
